<template>
  <!-- Mobile Backdrop -->
  <div
    v-if="isMobile && isMobileMenuOpen"
    class="mobile-backdrop"
    @click="closeMobileMenu"
  ></div>

  <aside class="admin-sidebar" :class="{
    collapsed: isCollapsed,
    open: isMobileMenuOpen,
    mobile: isMobile
  }">
    <div class="sidebar-toggle" @click="toggleSidebar">
      <i class="bi" :class="isCollapsed ? 'bi-chevron-right' : 'bi-chevron-left'"></i>
    </div>
    <nav class="sidebar-nav">
      <div class="nav-section">
        <h3 class="nav-section-title">Overview</h3>
        <ul class="nav-list">
          <li>
            <router-link to="/" class="nav-link" exact-active-class="active" title="Dashboard" @click="handleNavClick">
              <i class="bi bi-speedometer2"></i>
              <span class="nav-text">Dashboard</span>
            </router-link>
          </li>
        </ul>
      </div>

      <div class="nav-section">
        <h3 class="nav-section-title">Reporting</h3>
        <ul class="nav-list">
          <li>
            <router-link to="/booking-insights" class="nav-link" active-class="active" title="Booking Insights" @click="handleNavClick">
              <i class="bi bi-pie-chart"></i>
              <span class="nav-text">Booking Insights</span>
            </router-link>
          </li>
          <li>
            <router-link to="/returning-customers" class="nav-link" active-class="active" title="Returning Customers" @click="handleNavClick">
              <i class="bi bi-graph-up-arrow"></i>
              <span class="nav-text">Returning Customers</span>
            </router-link>
          </li>
          <li>
            <router-link to="/operations-report" class="nav-link" active-class="active" title="Ops Report" @click="handleNavClick">
              <i class="bi bi-clipboard-data"></i>
              <span class="nav-text">Ops Report</span>
            </router-link>
          </li>
          <li>
            <router-link to="/payroll-report" class="nav-link" active-class="active" title="Payroll Report" @click="handleNavClick">
              <i class="bi bi-cash-stack"></i>
              <span class="nav-text">Payroll Report</span>
            </router-link>
          </li>
        </ul>
      </div>

      <div class="nav-section">
        <h3 class="nav-section-title">Bookings</h3>
        <ul class="nav-list">
          <li>
            <router-link to="/bookings" class="nav-link" active-class="active" title="Bookings" @click="handleNavClick">
              <i class="bi bi-calendar-check"></i>
              <span class="nav-text">Bookings</span>
            </router-link>
          </li>
          <li>
            <router-link to="/calendar" class="nav-link" active-class="active" title="Calendar" @click="handleNavClick">
              <i class="bi bi-calendar-week"></i>
              <span class="nav-text">Calendar</span>
            </router-link>
          </li>
          <li>
            <router-link to="/booking-slots" class="nav-link" active-class="active" title="Booking Slots" @click="handleNavClick">
              <i class="bi bi-calendar3"></i>
              <span class="nav-text">Booking Slots</span>
            </router-link>
          </li>
          <li>
            <router-link to="/ai-recommendations" class="nav-link" active-class="active" title="AI Allocation Optimizer" @click="handleNavClick">
              <i class="bi bi-robot"></i>
              <span class="nav-text">AI Optimizer</span>
            </router-link>
          </li>
          <li>
            <router-link to="/recalls" class="nav-link" active-class="active" title="Recalls" @click="handleNavClick">
              <i class="bi bi-arrow-repeat"></i>
              <span class="nav-text">Recalls</span>
              <span v-if="pendingRecallsCount > 0" class="unread-badge">{{ pendingRecallsCount > 99 ? '99+' : pendingRecallsCount }}</span>
            </router-link>
          </li>
        </ul>
      </div>

      <div class="nav-section">
        <h3 class="nav-section-title">Customers</h3>
        <ul class="nav-list">
          <li>
            <router-link to="/customers" class="nav-link" active-class="active" title="Customers" @click="handleNavClick">
              <i class="bi bi-people"></i>
              <span class="nav-text">Customers</span>
            </router-link>
          </li>
          <li>
            <router-link to="/leads" class="nav-link" active-class="active" title="Leads" @click="handleNavClick">
              <i class="bi bi-person-plus"></i>
              <span class="nav-text">Leads</span>
            </router-link>
          </li>
          <li>
            <router-link to="/conversations" class="nav-link" active-class="active" title="SMS Chat" @click="handleNavClick">
              <i class="bi bi-chat-dots"></i>
              <span class="nav-text">SMS Chat</span>
              <span v-if="unreadSmsCount > 0" class="unread-badge">{{ unreadSmsCount > 99 ? '99+' : unreadSmsCount }}</span>
            </router-link>
          </li>
          <li>
            <router-link to="/ai-calls" class="nav-link" active-class="active" title="AI Calls" @click="handleNavClick">
              <i class="bi bi-robot"></i>
              <span class="nav-text">AI Calls</span>
            </router-link>
          </li>
        </ul>
      </div>

      <div class="nav-section">
        <h3 class="nav-section-title">Documents</h3>
        <ul class="nav-list">
          <li>
            <router-link to="/quotes" class="nav-link" active-class="active" title="Quotes" @click="handleNavClick">
              <i class="bi bi-file-earmark-text"></i>
              <span class="nav-text">Quotes</span>
            </router-link>
          </li>
          <li>
            <router-link to="/receipts" class="nav-link" active-class="active" title="Receipts" @click="handleNavClick">
              <i class="bi bi-receipt"></i>
              <span class="nav-text">Receipts</span>
            </router-link>
          </li>
          <li>
            <router-link to="/invoices" class="nav-link" active-class="active" title="Invoices" @click="handleNavClick">
              <i class="bi bi-file-earmark-text"></i>
              <span class="nav-text">Invoices</span>
            </router-link>
          </li>
          <li>
            <router-link to="/commercial-reports" class="nav-link" active-class="active" title="Commercial Reports" @click="handleNavClick">
              <i class="bi bi-file-earmark-medical"></i>
              <span class="nav-text">Commercial Reports</span>
            </router-link>
          </li>
          <li>
            <router-link to="/inspection-reports" class="nav-link" active-class="active" title="Inspection Reports" @click="handleNavClick">
              <i class="bi bi-clipboard-check"></i>
              <span class="nav-text">Inspection Reports</span>
            </router-link>
          </li>
        </ul>
      </div>

      <div class="nav-section">
        <h3 class="nav-section-title">Team</h3>
        <ul class="nav-list">
          <li>
            <router-link v-if="canManageUsers" to="/technicians" class="nav-link" active-class="active" title="Technicians" @click="handleNavClick">
              <i class="bi bi-person-gear"></i>
              <span class="nav-text">Technicians</span>
            </router-link>
            <span v-else class="nav-link nav-link-disabled" title="Requires super-admin access" aria-disabled="true">
              <i class="bi bi-person-gear"></i>
              <span class="nav-text">Technicians</span>
              <i class="bi bi-lock-fill nav-lock-icon"></i>
            </span>
          </li>
          <li>
            <router-link to="/timesheets" class="nav-link" active-class="active" title="Timesheets" @click="handleNavClick">
              <i class="bi bi-calendar-week"></i>
              <span class="nav-text">Timesheets</span>
            </router-link>
          </li>
          <li>
            <router-link v-if="canManageUsers" to="/vehicles" class="nav-link" active-class="active" title="Vehicles" @click="handleNavClick">
              <i class="bi bi-truck"></i>
              <span class="nav-text">Vehicles</span>
            </router-link>
            <span v-else class="nav-link nav-link-disabled" title="Requires super-admin access" aria-disabled="true">
              <i class="bi bi-truck"></i>
              <span class="nav-text">Vehicles</span>
              <i class="bi bi-lock-fill nav-lock-icon"></i>
            </span>
          </li>
        </ul>
      </div>

      <div class="nav-section">
        <h3 class="nav-section-title">Financial</h3>
        <ul class="nav-list">
          <li>
            <router-link to="/payments" class="nav-link" active-class="active" title="Payments" @click="handleNavClick">
              <i class="bi bi-credit-card"></i>
              <span class="nav-text">Payments</span>
            </router-link>
          </li>
        </ul>
      </div>

      <div class="nav-section">
        <h3 class="nav-section-title">Settings</h3>
        <ul class="nav-list">
          <li>
            <router-link v-if="canManageUsers" to="/user-management" class="nav-link" active-class="active" title="User Management" @click="handleNavClick">
              <i class="bi bi-person-lock"></i>
              <span class="nav-text">User Management</span>
            </router-link>
            <span v-else class="nav-link nav-link-disabled" title="Requires super-admin access" aria-disabled="true">
              <i class="bi bi-person-lock"></i>
              <span class="nav-text">User Management</span>
              <i class="bi bi-lock-fill nav-lock-icon"></i>
            </span>
          </li>
        </ul>
      </div>
    </nav>
  </aside>
</template>

<script>
import { useAuth0 } from '@auth0/auth0-vue'
import { conversationAPI, recallsAPI } from '@/services/api'
import { hasSuperAdminAccess } from '@/utils/auth'

export default {
  name: 'AdminSidebar',
  setup() {
    const { user } = useAuth0()
    return { auth0User: user }
  },
  props: {
    mobileMenuOpen: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isCollapsed: localStorage.getItem('sidebarCollapsed') === 'true',
      isMobile: false,
      unreadSmsCount: 0,
      pendingRecallsCount: 0,
      unreadPollInterval: null
    }
  },
  computed: {
    isMobileMenuOpen() {
      return this.mobileMenuOpen
    },
    canManageUsers() {
      return hasSuperAdminAccess(this.auth0User)
    }
  },
  methods: {
    toggleSidebar() {
      this.isCollapsed = !this.isCollapsed
      localStorage.setItem('sidebarCollapsed', this.isCollapsed)
      this.$emit('toggle', this.isCollapsed)
    },
    closeMobileMenu() {
      this.$emit('mobile-close')
    },
    handleNavClick() {
      // Auto-close mobile menu when nav link is clicked
      if (this.isMobile && this.isMobileMenuOpen) {
        this.closeMobileMenu()
      }
    },
    checkIfMobile() {
      this.isMobile = window.innerWidth <= 768
      if (!this.isMobile && this.isMobileMenuOpen) {
        this.closeMobileMenu()
      }
    },
    handleResize() {
      this.checkIfMobile()
    },
    async fetchUnreadCount() {
      try {
        const response = await conversationAPI.getConversations({ limit: 1 })
        this.unreadSmsCount = response.data?.unread_count || 0
      } catch (error) {
        // Silently fail - don't disrupt sidebar for unread count
        console.error('Failed to fetch unread SMS count:', error)
      }
    },
    async fetchPendingRecallsCount() {
      try {
        const params = new URLSearchParams({ status: 'pending', limit: 1 })
        const response = await recallsAPI.getRecalls(params)
        this.pendingRecallsCount = response.data?.pagination?.total || 0
      } catch (error) {
        // Silently fail - don't disrupt sidebar for the recalls count
        console.error('Failed to fetch pending recalls count:', error)
      }
    },
    startUnreadPolling() {
      // Poll every 30 seconds for new unread messages / pending recalls
      this.unreadPollInterval = setInterval(() => {
        this.fetchUnreadCount()
        this.fetchPendingRecallsCount()
      }, 30000)
    }
  },
  mounted() {
    this.$emit('toggle', this.isCollapsed)
    this.checkIfMobile()
    window.addEventListener('resize', this.handleResize)
    // Fetch initial counts and start polling
    this.fetchUnreadCount()
    this.fetchPendingRecallsCount()
    this.startUnreadPolling()
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize)
    // Clear polling interval
    if (this.unreadPollInterval) {
      clearInterval(this.unreadPollInterval)
    }
  }
}
</script>

<style scoped>
/* Mobile Backdrop */
.mobile-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 999;
  backdrop-filter: blur(2px);
}

.admin-sidebar {
  width: 260px;
  background-color: var(--secondary);
  border-right: 1px solid var(--border-light);
  padding: 3rem 0;
  overflow-y: auto;
  position: relative;
  transition: width 0.3s ease;
}

.admin-sidebar.collapsed {
  width: 70px;
}

.sidebar-toggle {
  position: absolute;
  top: 1rem;
  right: 0.5rem;
  width: 24px;
  height: 24px;
  background-color: var(--primary);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  transition: var(--transition);
  box-shadow: var(--shadow-md);
}

.sidebar-toggle:hover {
  background-color: var(--primary-dark);
  transform: scale(1.1);
}

.sidebar-toggle i {
  font-size: 0.75rem;
}

.sidebar-nav {
  padding: 0 1rem;
}

.nav-section {
  margin-bottom: 2rem;
}

.nav-section-title {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #cbd5e1;
  margin-bottom: 0.75rem;
  padding: 0 0.75rem;
}

.nav-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.nav-list li {
  margin-bottom: 0.25rem;
}

.nav-link {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  color: #e0e7ef;
  text-decoration: none;
  border-radius: var(--radius-sm);
  font-size: 0.875rem;
  font-weight: 500;
  transition: var(--transition);
}

.nav-link:hover {
  background-color: var(--primary-light);
  color: var(--primary-dark);
  transform: translateX(2px);
}

.nav-link.active {
  background-color: var(--primary);
  color: white;
  box-shadow: var(--shadow-sm);
}

.nav-link.active:hover {
  background-color: var(--primary-dark);
  transform: translateX(2px);
}

.nav-link-disabled {
  color: #94a3b8;
  background-color: rgba(148, 163, 184, 0.08);
  cursor: not-allowed;
  opacity: 0.65;
}

.nav-link-disabled:hover {
  color: #94a3b8;
  background-color: rgba(148, 163, 184, 0.08);
  transform: none;
}

.nav-link i {
  font-size: 1.125rem;
  width: 1.25rem;
  text-align: center;
}

.nav-link span {
  flex: 1;
}

.nav-lock-icon {
  flex: 0 0 auto;
  width: auto !important;
  font-size: 0.75rem !important;
  opacity: 0.8;
}

/* Unread SMS Badge */
.unread-badge {
  background-color: #ef4444;
  color: white;
  font-size: 0.7rem;
  font-weight: 600;
  padding: 0.15rem 0.4rem;
  border-radius: 9999px;
  min-width: 1.25rem;
  text-align: center;
  flex-shrink: 0;
  animation: pulse-badge 2s infinite;
}

@keyframes pulse-badge {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}

/* Collapsed state styles - DESKTOP ONLY */
.admin-sidebar.collapsed:not(.mobile) .nav-section-title {
  opacity: 0;
  visibility: hidden;
  font-size: 0;
  margin: 0;
  padding: 0;
}

.admin-sidebar.collapsed:not(.mobile) .nav-text {
  display: none;
}

/* Unread badge in collapsed state - position as dot on icon */
.admin-sidebar.collapsed:not(.mobile) .unread-badge {
  position: absolute;
  top: 0.25rem;
  right: 0.25rem;
  padding: 0.1rem 0.25rem;
  font-size: 0.6rem;
  min-width: 1rem;
}

.admin-sidebar.collapsed:not(.mobile) .nav-link {
  justify-content: center;
  padding: 0.75rem;
}

.admin-sidebar.collapsed:not(.mobile) .nav-link i {
  font-size: 1.25rem;
  margin: 0;
}

.admin-sidebar.collapsed:not(.mobile) .nav-lock-icon {
  display: none;
}

.admin-sidebar.collapsed:not(.mobile) .sidebar-nav {
  padding: 0 0.5rem;
}

.admin-sidebar.collapsed:not(.mobile) .nav-section {
  margin-bottom: 1rem;
}

/* Tooltip for collapsed sidebar - DESKTOP ONLY */
.admin-sidebar.collapsed:not(.mobile) .nav-link {
  position: relative;
}

.admin-sidebar.collapsed:not(.mobile) .nav-link::after {
  content: attr(title);
  position: absolute;
  left: 100%;
  top: 50%;
  transform: translateY(-50%);
  margin-left: 1rem;
  background-color: var(--secondary);
  color: white;
  padding: 0.5rem 0.75rem;
  border-radius: var(--radius-sm);
  white-space: nowrap;
  font-size: 0.875rem;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s;
  z-index: 1000;
  box-shadow: var(--shadow-lg);
}

.admin-sidebar.collapsed:not(.mobile) .nav-link:hover::after {
  opacity: 1;
}

/* Mobile-specific styles */
.admin-sidebar.mobile {
  width: 280px;
  position: fixed;
  top: 0;
  left: -100%;
  height: 100vh;
  z-index: 1000;
  transition: left 0.3s ease;
  max-width: 80vw;
}

.admin-sidebar.mobile.open {
  left: 0;
}

/* Hide desktop toggle on mobile */
.admin-sidebar.mobile .sidebar-toggle {
  display: none;
}

/* Ensure mobile menu always shows text and proper layout */
.admin-sidebar.mobile .nav-text {
  display: block !important;
}

.admin-sidebar.mobile .nav-section-title {
  opacity: 1 !important;
  visibility: visible !important;
  font-size: 0.75rem !important;
  margin-bottom: 0.75rem !important;
  padding: 0 0.75rem !important;
}

.admin-sidebar.mobile .nav-link {
  justify-content: flex-start !important;
  padding: 0.75rem !important;
}

.admin-sidebar.mobile .nav-link i {
  font-size: 1.125rem !important;
  margin: 0 !important;
}

.admin-sidebar.mobile .sidebar-nav {
  padding: 0 1rem !important;
}

.admin-sidebar.mobile .nav-section {
  margin-bottom: 2rem !important;
}

/* Responsive design */
@media (max-width: 768px) {
  .admin-sidebar {
    width: 280px;
    position: fixed;
    top: 0;
    left: -100%;
    height: 100vh;
    z-index: 1000;
    transition: left 0.3s ease;
    max-width: 80vw;
  }

  .admin-sidebar.open {
    left: 0;
  }

  /* Hide desktop toggle on mobile */
  .admin-sidebar .sidebar-toggle {
    display: none;
  }
}
</style>
